<template>
  <div class="row">
    <InputDatePicker
      v-model="localDate"
      v-model:range="localRange"
      :active="active"
      :active-color="activeColor"
      :allow-all-time="allowAllTime"
      bordered
      :clearable="clearable"
      close-on-update
      :date-format="dateFormat"
      :date-style="localDateStyle"
      :hide-user-time-zone="hideUserTimeZone"
      :label="rangeLabel"
      :max-date="maxDate"
      :placeholder="placeholder"
      :range-format="dateFormat"
      :range-selection="type"
      show-date-input
      :show-previous-intervals="doublePanel"
      :show-range-options="type !== 'day'"
      :show-seconds="showSeconds"
      :show-time-input="!hideTimeInput"
      show-update-button
      :size="size"
      :time-style="localTimeStyle"
      use-local-time-zone
    >
      <template
        v-if="showNowButton"
        #action="{ setCustomDate }"
      >
        <BaseButton
          label="Now"
          new-design
          @click="now(setCustomDate)"
        />
      </template>
    </InputDatePicker>

    <TimezoneDropdown
      v-if="!hideTimezone"
      v-model="localTimezone"
      class="timezone"
      hide-label
      new-design
    />
  </div>
</template>

<script>
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import { BaseButton, InputDatePicker } from "shared/components/base";
import TimezoneDropdown from "shared/components/core/pickers/TimezoneDropdown.vue";
import { fromCurrentToGivenTimezone, getTimezone } from "shared/helpers/date";
import DateRange from "shared/helpers/DateRange";

export default {
  name: "TimezoneRangePicker",
  components: {
    TimezoneDropdown,
    InputDatePicker,
    BaseButton,
  },
  props: {
    type: {
      type: String,
      default: "custom",
    },
    modelValue: {
      type: [Date, DateRange],
      default: () => DateRange.today(),
    },
    dateFormat: {
      type: String,
      default: null,
    },
    dateStyle: {
      type: String,
      default: null,
      validator(value) {
        return [null, "long", "medium", "short", "none"].includes(value);
      },
    },
    timeStyle: {
      type: String,
      default: null,
      validator(value) {
        return [null, "medium", "short", "none"].includes(value);
      },
    },
    timezone: {
      type: String,
      default: null,
    },
    hideTimezone: {
      type: Boolean,
    },
    hideUserTimeZone: {
      type: Boolean,
    },
    hideTimeInput: {
      type: Boolean,
    },
    maxDate: {
      type: Date,
      default: null,
    },
    allowAllTime: {
      type: Boolean,
    },
    doublePanel: {
      type: Boolean,
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    showSeconds: {
      type: Boolean,
    },
    hideNowButton: {
      type: Boolean,
    },
    clearable: {
      type: Boolean,
    },
    active: {
      type: Boolean,
    },
    activeColor: {
      type: String,
      default: "activated",
    },
    size: {
      type: String,
      default: "md",
    },
  },
  emits: ["update:modelValue", "update:timezone"],
  data() {
    return {
      localTimezone: this.timezone || getTimezone(),
    };
  },
  computed: {
    localDateStyle() {
      if (this.dateFormat || this.dateStyle === "none") {
        return null;
      }

      return this.dateStyle || "short";
    },
    localTimeStyle() {
      if (this.dateFormat || this.timeStyle === "none") {
        return null;
      }

      return this.timeStyle || "short";
    },
    rangeLabel() {
      if (this.modelValue) return "";

      if (this.label) {
        return this.label;
      }

      if (this.placeholder) {
        return this.placeholder;
      }

      return "Start Date - End Date";
    },
    localDate: {
      get() {
        if (!this.modelValue || this.type !== "day") {
          return null;
        }

        return fromCurrentToGivenTimezone(this.modelValue, this.localTimezone);
      },
      set(value) {
        if (value) {
          this.$emit(
            "update:modelValue",
            zonedTimeToUtc(value, this.localTimezone)
          );

          return;
        }

        this.$emit("update:modelValue", null);
      },
    },
    localRange: {
      get() {
        if (!this.modelValue || this.type === "day") {
          return null;
        }

        const before = fromCurrentToGivenTimezone(
          new Date(this.modelValue.before * 1000),
          this.localTimezone
        );

        const after = fromCurrentToGivenTimezone(
          new Date(this.modelValue.after * 1000),
          this.localTimezone
        );

        return DateRange.fromDates(after, before);
      },
      set(value) {
        if (value) {
          const after = zonedTimeToUtc(
            new Date(value.after * 1000),
            this.localTimezone
          );

          const before = zonedTimeToUtc(
            new Date(value.before * 1000),
            this.localTimezone
          );

          this.$emit("update:modelValue", DateRange.fromDates(after, before));

          return;
        }

        this.$emit("update:modelValue", null);
      },
    },
    showNowButton() {
      return !this.hideNowButton && this.type === "day";
    },
  },
  watch: {
    timezone(value) {
      this.localTimezone = value || getTimezone();
    },
    localTimezone(value) {
      this.$emit("update:timezone", value);
    },
  },
  methods: {
    now(setCustomDate) {
      setCustomDate(utcToZonedTime(Date.now(), this.localTimezone));
    },
  },
};
</script>

<style lang="scss" scoped>
.timezone {
  padding-left: 10px;
  width: 230px;
}
</style>
